import { Injectable } from '@angular/core';
import { formatDate } from '@angular/common';
import {
    AdminMusicalInformationDto,
    AdminReservationDto,
    AdminShowInformationDto,
    AdminAreaDto
} from '../models/admin-api.model';
import {
    ReservationItem,
    ReservationOrderItem,
    ShowDetail,
    ShowListItem
} from '../models/show.model';
import {
    extractShowBlocks,
    mapReservationSource,
    mapReservationStatus,
    mapSlotLabel,
    mapSlotTimeRange,
    normalizeTime
} from '../utils/admin-enum.utils';

@Injectable({
    providedIn: 'root'
})
export class AdminMapperService {
    toVenueName(info: AdminMusicalInformationDto | null | undefined): string {
        return info?.storeGroupName?.trim() || '—';
    }

    toMusicalName(
        info: AdminMusicalInformationDto | null | undefined,
        fallbackShowCode?: string | null
    ): string {
        return info?.musicalName?.trim() || fallbackShowCode || 'Show';
    }

    toShowListItems(
        shows: AdminShowInformationDto[] | null | undefined,
        reservations: AdminReservationDto[] | null | undefined,
        musicalInfo: AdminMusicalInformationDto | null | undefined
    ): ShowListItem[] {
        const reservationList = reservations ?? [];

        return (shows ?? []).map(show => {
            const allShowReservations = reservationList
                .filter(r => r.showID === show.showID);

            const activeGuests = reservationList
                .filter(r => r.showID === show.showID && mapReservationStatus(r) !== 'Storniert')
                .reduce((sum, item) => sum + (item.reservedCount ?? 0), 0);

            return {
                id: show.showID,
                title: show.showCode?.trim() || 'Show',
                date: show.showDate || '',
                timeLabel: normalizeTime(show.showTime),
                blocks: extractShowBlocks(show),
                reservations: allShowReservations.length,
                guests: activeGuests
            };
        });
    }

    toShowDetail(
        show: AdminShowInformationDto,
        reservations: AdminReservationDto[],
        musicalInfo: AdminMusicalInformationDto | null | undefined
    ): ShowDetail {
        const relatedReservations = reservations
            .filter(r => r.showID === show.showID)
            .map(r => this.toReservationItem(r, show, musicalInfo));

        const confirmedReservations = relatedReservations.filter(r => r.status === 'Bestätigt').length;
        const canceledReservations = relatedReservations.filter(
            r => r.status === 'Storniert' || r.status === 'Benutzerstorno'
        ).length;

        const guests = relatedReservations
            .filter(r => r.status !== 'Storniert' && r.status !== 'Benutzerstorno')
            .reduce((sum, r) => sum + r.persons, 0);

        const occupiedTables = this.calculateOccupiedTables(show, reservations);

        return {
            id: show.showID,
            title: show.showCode?.trim() || 'Show',
            dateLabel: show.showDate ? formatDate(show.showDate, 'EEE, dd.MM.', 'de-DE') : '—',
            timeLabel: normalizeTime(show.showTime),
            blocks: extractShowBlocks(show),
            occupiedTables,
            confirmedReservations,
            guests,
            canceledReservations,
            reservationsCount: relatedReservations.length,
            reservations: relatedReservations
        };
    }

    toReservationItem(
        reservation: AdminReservationDto,
        show: AdminShowInformationDto,
        musicalInfo: AdminMusicalInformationDto | null | undefined
    ): ReservationItem {
        const source = mapReservationSource(reservation.source);
        const status = reservation.wasCancelledByUser
            ? 'Benutzerstorno'
            : mapReservationStatus(reservation);
        const orderItems: ReservationOrderItem[] = (reservation.order?.positions ?? []).map((item, index) => ({
            id: Number(item.itemID ?? index + 1),
            name: item.itemName || 'Artikel',
            quantity: item.quantity ?? 0,
            price: this.formatEuro(item.totalPrice ?? 0)
        }));

        return {
            id: this.hashString(reservation.processID || `reservation-${show.showID}`),
            processId: reservation.processID || '',
            showId: reservation.showID ?? show.showID,
            areaId: reservation.areaID ?? null,
            slotKey: reservation.slot ?? null,
            sourceRaw: reservation.source ?? null,
            targetPlaceType: reservation.targetPlaceType ?? null,
            isArchived: !!reservation.isArchived,
            wasCancelled: !!reservation.wasCancelled,
            wasCancelledByUser: !!reservation.wasCancelledByUser,
            rebookedByAdminAt: reservation.rebookedByAdminAt ?? null,

            reservationCode: reservation.order?.retrievalCode
                ? `Reservierung ${reservation.order.retrievalCode}`
                : '—',
            guestName: this.getDisplayGuestName(reservation),
            realGuestName: this.getRealGuestName(reservation),
            phone: reservation.user?.phoneNumber || '—',
            email: reservation.user?.email || '—',
            persons: reservation.reservedCount ?? 0,
            table: reservation.targetTableNumber != null ? `T${reservation.targetTableNumber}` : '—',
            block: mapSlotLabel(reservation.slot),
            blockTime: mapSlotTimeRange(reservation.slot, show),
            amount: this.formatEuro(reservation.order?.totalPrice ?? 0),
            status,
            source,
            canCancel:
                source !== 'Kasse' &&
                status !== 'Storniert' &&
                status !== 'Benutzerstorno' &&
                !reservation.isArchived,
            showTitle: show.showCode?.trim() || 'Show',
            showDateIso: show.showDate || '',
            showDateLabelLong: show.showDate ? formatDate(show.showDate, 'dd.MM.yyyy', 'de-DE') : '—',
            venueName: musicalInfo?.storeGroupName?.trim() || '—',
            fixedTableRequested: reservation.targetTableNumber != null,
            orderLabel: source === 'Kasse' ? 'Kassenbestellung' : source === 'Admin' ? 'Adminbuchung' : 'Webbestellung',
            orderItems
        };
    }

    toRebookShowOptionLabel(show: ShowListItem): string {
        return `${show.title} – ${formatDate(show.date, 'EEE, dd.MM.', 'de-DE')} ${show.timeLabel} Uhr`;
    }

    toRebookBlockOptionLabel(block: string, timeRange: string): string {
        return `${block} (${timeRange})`;
    }

    private formatEuro(value: number): string {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(value);
    }

    private getRealGuestName(reservation: AdminReservationDto): string {
        const user = reservation.user as any;

        const first = user?.firstName?.trim() || '';
        const last = user?.lastName?.trim() || '';

        return `${first} ${last}`.trim() || '—';
    }

    private getDisplayGuestName(reservation: AdminReservationDto): string {
        const user = reservation.user as any;

        const reservationName =
            user?.reservationName?.trim() ||
            user?.ReservationName?.trim() ||
            '';

        if (reservationName) {
            return reservationName;
        }

        return this.getRealGuestName(reservation);
    }

    private calculateOccupiedTables(show: AdminShowInformationDto, reservations: AdminReservationDto[]): string {
        const related = reservations.filter(r => r.showID === show.showID && !r.wasCancelled);

        const uniqueTables = new Set(
            related
                .map(r => r.targetTableNumber)
                .filter((table): table is number => table != null)
        );

        const totalTables = this.getAllTablesForShow(show).length;

        return `${uniqueTables.size} / ${totalTables || 0}`;
    }

    private getAllTablesForShow(show: AdminShowInformationDto): NonNullable<AdminAreaDto['tables']> {
        const beforeAreas = show.tableRoomPlan?.areasBySlot?.Before ?? [];
        const intermissionAreas = show.tableRoomPlan?.areasBySlot?.Intermission ?? [];
        const afterAreas = show.tableRoomPlan?.areasBySlot?.After ?? [];

        const allAreas = [...beforeAreas, ...intermissionAreas, ...afterAreas];

        const uniqueTables = new Map<number, NonNullable<AdminAreaDto['tables']>[number]>();

        for (const area of allAreas) {
            for (const table of area.tables ?? []) {
                if (table.tableNumber != null && !uniqueTables.has(table.tableNumber)) {
                    uniqueTables.set(table.tableNumber, table);
                }
            }
        }

        return Array.from(uniqueTables.values());
    }

    private hashString(value: string): number {
        let hash = 0;

        for (let i = 0; i < value.length; i++) {
            hash = ((hash << 5) - hash) + value.charCodeAt(i);
            hash |= 0;
        }

        return Math.abs(hash);
    }
}